/*
 * Copyright 2026 Jason Figge
 *
 * This file is part of Chip Hippo.
 *
 * Chip Hippo is free software: you can redistribute it and/or modify it under
 * the terms of the GNU General Public License as published by the Free
 * Software Foundation, either version 3 of the License, or (at your option)
 * any later version.
 *
 * Chip Hippo is distributed in the hope that it will be useful, but WITHOUT
 * ANY WARRANTY; without even the implied warranty of MERCHANTABILITY or
 * FITNESS FOR A PARTICULAR PURPOSE. See the GNU General Public License for
 * more details.
 *
 * You should have received a copy of the GNU General Public License along
 * with Chip Hippo. If not, see <https://www.gnu.org/licenses/>.
 */

// discretes.js — the discretes: capacitors, inductors, diodes and
// transistors, each in its own COMPONENTS group. Pure data and pure functions,
// like every def in catalog/parts.js, which spreads this list into PART_DEFS.
//
// The tray marks all four groups — a red (i) saying these parts have limited
// functionality and exist for export and a complete design
// (palette-panel.js, which finds them by `countsAsConnection`, below) —
// while the digital engine is selected; Spice Lite simulates them, so it
// drops the mark.
//
// What the four have in common is the bargain each strikes with a LOGIC
// simulator: it does as much as a digital, event-driven engine can honestly
// represent, and no more — and it exports to KiCad as the real part, which is
// what makes it worth placing at all. So:
//
//   capacitor   joins NOTHING (a charged capacitor blocks DC); timing parts
//               read its value off the wiring (sim/rc-trace.js).
//   inductor    conducts like a WIRE (a coil at DC is a length of copper);
//               the digital engine never simulates its inductance (Spice
//               Lite does: sim/spice/inductors.js).
//   diode       ONE-WAY (`oneWayBridges`): a HIGH on the anode passes to the
//               cathode at the strength it arrived with; nothing passes back.
//   transistor  a SWITCH its base or gate opens and closes — an analog-switch
//               channel (sim/analog-switch.js `transistorSwitch`), the very
//               mechanism the CD4066B uses.
//
// No forward drop, Zener breakdown, gain, threshold or kickback: analog, and
// out of scope. Every part here carries an optional Part number, printed on
// the part and exported, which changes nothing about how it behaves.
//
// And every part here COUNTS AS A CONNECTION (`countsAsConnection`): a pin
// whose only company is one of them is wired, whether or not the part is
// conducting right now — a reversed diode and an off transistor are a valid
// board, not an unconnected pin (sim/rc-trace.js `connectedByPart`).

import { normalizeLeadOffset } from "./lead-offset.js";
import {
  ZENER_VOLTS_FIELD,
  partTypeField,
  storedValue,
  transistorPartField,
  valueField,
} from "./value-fields.js";
import {
  CERAMIC_VALUES,
  ELECTROLYTIC_VALUES,
  INDUCTOR_VALUES,
  VALUE_RANGES,
  e12Series,
  formatComponentValue,
  transistorTypeOf,
} from "../model/component-value.js";
import { transistorSwitch } from "../sim/analog-switch.js";
import {
  FIGURE_FIELDS,
  figureKind,
  gradeFigures,
  isGradeFigures,
} from "../sim/spice/transistor-figures.js";
import { H, L } from "../sim/levels.js";

/** The longest part number kept — longer than any a maker prints. */
const MAX_PART_NUMBER = 32;

/**
 * The Part number field every one of the discretes carries: free text (1N4148,
 * 2N2222, BZX79-C5V1…), printed on the part and written to the export. It is a
 * LABEL, as a resistor's colour is not: nothing in the simulation reads it.
 */
export const PART_NUMBER_FIELD = Object.freeze({
  key: "partNumber",
  label: "Part number",
  type: "text",
});

/**
 * A part's typed part number, trimmed and capped, or null when it has none —
 * the one place it is coerced, so every reader (the desk, the BOM, the
 * schematic, the export) sees the same text.
 * @param {{partNumber?: unknown}} [params]
 * @returns {string|null}
 */
export function partNumberOf(params) {
  const text =
    typeof params?.partNumber === "string" ? params.partNumber.trim() : "";
  return text ? text.slice(0, MAX_PART_NUMBER) : null;
}

/** `params` with the part number laid on, stored only when there is one — so
    a part that never had one round-trips byte-identical. */
function withPartNumber(params, raw) {
  const partNumber = partNumberOf(raw);
  return partNumber ? { ...params, partNumber } : params;
}

/** The two-free-ends geometry every two-lead part here keeps, as the resistor
    and the LED do (see normalizeLeadOffset). */
function leadGeometry(raw) {
  const rotated = raw?.rot === 90;
  return {
    rot: rotated ? 90 : 0,
    end: rotated ? normalizeLeadOffset(raw?.end) : null,
  };
}

// ── Capacitors ──────────────────────────────────────────────────────────────

/** A capacitor's Type: the two are one part to the user, swapped in place
    (value-fields.js `partTypeField`). */
const CAPACITORS = Object.freeze(["cap-ceramic", "cap-electrolytic"]);
const CAPACITOR_TYPE_FIELD = partTypeField([
  { value: "cap-ceramic", label: "Ceramic" },
  { value: "cap-electrolytic", label: "Electrolytic" },
]);

/** Each capacitor's Capacitance: its own range and its own common values —
    and, for the nearest-value hint, the E12 values it is made in across that
    range (its list is a handful; 4.7n is no less standard for missing it). */
const capacitanceField = (range, values) =>
  valueField({
    key: "farads",
    label: "Capacitance",
    unit: "farad",
    range,
    values,
    series: e12Series(range),
  });

/**
 * A capacitor's params: its value, plus the same two-free-ends geometry the
 * resistor and LED keep (`rot`, `end` — see normalizeLeadOffset). Any positive
 * value is KEPT, in range or not (a ceramic swapped for an electrolytic keeps
 * its value, and its card says if the new type cannot be that); text an older
 * document holds is read, or kept as it was (value-fields.js `storedValue`).
 */
function capacitorParams(raw, fallback) {
  return withPartNumber(
    {
      farads: storedValue(raw?.farads, "farad", fallback),
      ...leadGeometry(raw),
    },
    raw,
  );
}

/** What both capacitors' blurbs say about what a capacitor IS here. */
const CAPACITOR_NOTE =
  "In this logic sim a capacitor joins nothing — it passes no current, " +
  "between any two holes, rails included (a charged capacitor blocks DC) — " +
  "and it does not filter, smooth or store charge. What it carries is its " +
  "VALUE: a timing part (the 555, or one of the 4000-series timers) reads " +
  "it off the wiring, and the KiCad export writes it. A pin whose only " +
  "company is a capacitor is not called unconnected.";

// ── Inductors ───────────────────────────────────────────────────────────────

/**
 * The Inductance field — OPTIONAL, unlike a capacitance: a bare inductor is a
 * part you can place, and clearing the box clears the value rather than
 * being refused.
 */
const INDUCTANCE_FIELD = valueField({
  key: "henries",
  label: "Inductance",
  unit: "henry",
  range: VALUE_RANGES.inductor,
  values: INDUCTOR_VALUES,
  optional: true,
});

/**
 * Which inductor it is, the first the default: a TOROID ("coil" — copper
 * wound round a ferrite ring, standing on its two leads) or a DRUM in a black
 * can, the leads out of its base. The desk draws it and the export picks its
 * footprint by it (kicad-parts.js); the simulation never reads it.
 */
export const INDUCTOR_STYLES = Object.freeze(["coil", "can"]);

const INDUCTOR_STYLE_FIELD = Object.freeze({
  key: "style",
  label: "Style",
  type: "segmented",
  options: Object.freeze([
    Object.freeze({ value: "coil", label: "Coil" }),
    Object.freeze({ value: "can", label: "Can" }),
  ]),
});

/**
 * How many holes an inductor's body covers between its leads: 1 puts the
 * leads 0.2 in (5.08 mm) apart under the smallest body, 2 (the default) 0.3 in
 * (7.62 mm), 3 0.4 in (10.16 mm) under the biggest. Unlike the style it MOVES
 * a pin: lying along a row, pin 2 lands as many holes on (`offsetsFor`,
 * below); stood up on two free ends, only the body changes.
 */
export const INDUCTOR_BODY_HOLES = Object.freeze([1, 2, 3]);

/** The holes between a new inductor's leads — the size every inductor had
    before the 1-hole one was added, so a fresh part places as it always has. */
export const INDUCTOR_DEFAULT_HOLES = 2;

const BODY_HOLES_FIELD = Object.freeze({
  key: "bodyHoles",
  label: "Holes between leads",
  type: "segmented",
  options: Object.freeze(
    INDUCTOR_BODY_HOLES.map((n) => Object.freeze({ value: n, label: `${n}` })),
  ),
  // It can move a pin, so it is a topology edit: greyed while the circuit
  // runs (desk-controller.js #propertyFieldsFor), and refused — saying this
  // — where the part sits has no room for it (DeskDoc.canSetComponentParams).
  movesPins: true,
  refused:
    "No room — the hole its lead would move to is taken, or past the end " +
    "of the board.",
});

/** An inductor's lead offsets, by the holes between its leads. */
const INDUCTOR_OFFSETS = Object.freeze({
  1: Object.freeze([0, 2]),
  2: Object.freeze([0, 3]),
  3: Object.freeze([0, 4]),
});

/** The holes between an inductor's leads, `params.bodyHoles` when it is one
    of the sizes, else the default — the one read for the offsets, the
    drawing and the export, so they can never disagree. */
export const inductorHoles = (params) =>
  INDUCTOR_BODY_HOLES.includes(params?.bodyHoles)
    ? params.bodyHoles
    : INDUCTOR_DEFAULT_HOLES;

/**
 * An inductor's winding resistance (DCR), Ω, as a power of its inductance:
 * R = `ohms1mH` · (L / 1 mH)^`p` — a maker's series does not hold R/L
 * constant (finer wire on the bigger values), so a fixed ohms-per-henry
 * would be wrong by a factor of several across a series. Fitted per body
 * and size to the TYPICAL winding:
 *   can (a radial drum)  1 hole: Bourns RLB0914 (8.7 mm, 5 mm pitch),
 *                        28 values 3.3 µH–1 mH; 2 and 3 holes: Bourns
 *                        RLB1314 (11.7 mm, 7 mm pitch), 23 values
 *                        3.3 µH–15 mH (the series' largest body);
 *   coil (a toroid)      3 holes: Bourns 2100 series (21.8 mm), 10 µH 6 mΩ,
 *                        22 µH 15 mΩ, 330 µH 230 mΩ, 1 mH 400 mΩ; 2 and 1
 *                        hole scaled from it by size (16 and 10 mm: at one
 *                        inductance a winding's resistance goes as 1/size²).
 * Each series spreads ±40 % about its fit; the WINDING grades span that.
 */
export const INDUCTOR_WINDING_FITS = Object.freeze({
  can: Object.freeze({
    1: Object.freeze({ ohms1mH: 1.946, p: 0.801 }),
    2: Object.freeze({ ohms1mH: 1.213, p: 0.949 }),
    3: Object.freeze({ ohms1mH: 1.213, p: 0.949 }),
  }),
  coil: Object.freeze({
    1: Object.freeze({ ohms1mH: 1.9, p: 0.91 }),
    2: Object.freeze({ ohms1mH: 0.74, p: 0.91 }),
    3: Object.freeze({ ohms1mH: 0.4, p: 0.91 }),
  }),
});

/** The winding grades (Spice Lite's Winding field), each a factor on its
    body's typical resistance. */
export const INDUCTOR_WINDINGS = Object.freeze(["lowest", "typical", "higher", "highest"]); // prettier-ignore
export const WINDING_FACTOR = Object.freeze({
  lowest: 0.6,
  typical: 1,
  higher: 1.6,
  highest: 2.5,
});
export const DEFAULT_WINDING = "typical";

/** An inductor's winding grade: `params.winding` when it is one, else
    Typical. */
export const inductorWinding = (params) =>
  INDUCTOR_WINDINGS.includes(params?.winding) ? params.winding : DEFAULT_WINDING; // prettier-ignore

/** An inductor's DC resistance, Ω, at a grade (its own, by default) — or
    null with no inductance (a wire). */
export function inductorOhms(params, winding = inductorWinding(params)) {
  const henries = Number(params?.henries);
  if (!(henries > 0)) return null;
  const style = INDUCTOR_STYLES.includes(params?.style) ? params.style : INDUCTOR_STYLES[0]; // prettier-ignore
  const fit = INDUCTOR_WINDING_FITS[style][inductorHoles(params)];
  return fit.ohms1mH * (henries / 1e-3) ** fit.p * WINDING_FACTOR[winding];
}

/** Each winding grade's English name (`properties.option.<grade>`). */
const WINDING_LABELS = Object.freeze({
  lowest: "Lowest",
  typical: "Typical",
  higher: "Higher",
  highest: "Highest",
});

/**
 * Spice Lite's Winding field: which of its body's windings the part has, each
 * option stating the resistance it gives at the part's CURRENT values
 * (`detail`, re-asked by the dialog after every change, so a new Inductance,
 * style or size moves it). Shown only while Spice Lite is on (`spiceOnly`,
 * desk-controller.js #propertyFieldsFor) — the digital engine runs an
 * inductor as a wire — and greyed while the Inductance is blank, when the
 * part is a wire in both engines. It moves no pin, so it stays live while the
 * circuit runs.
 */
const WINDING_FIELD = Object.freeze({
  key: "winding",
  label: "Winding",
  type: "select",
  default: DEFAULT_WINDING,
  spiceOnly: true,
  options: (values) =>
    INDUCTOR_WINDINGS.map((winding) => {
      const ohms = inductorOhms(values, winding);
      return {
        value: winding,
        label: WINDING_LABELS[winding],
        detail: ohms == null ? null : formatComponentValue(Number(ohms.toPrecision(2)), "ohm"), // prettier-ignore
      };
    }),
  disabledWhen: (values) => !(Number(values.henries) > 0),
});

// ── Diodes ──────────────────────────────────────────────────────────────────

/**
 * A diode's GRADES (Jason, 2026-10-19: "a range of diodes to pick from, a
 * common diode as the default") — each one representative part's figures,
 * as the transistors' are (spice/diodes.js `DIODE_GRADE_SPECS`): the
 * small-signal 1N4148 (the default, and every diode before there were
 * grades), the 1 A 1N4001–1N4007 rectifier, the 3 A 1N5400–1N5408 and the
 * 1N5817–1N5819 Schottky. `part` is shown beside each.
 */
export const DIODE_GRADES = Object.freeze([
  Object.freeze({ value: "small-signal", label: "Small signal", part: "1N4148" }), // prettier-ignore
  Object.freeze({ value: "rectifier", label: "Rectifier", part: "1N4001–1N4007" }), // prettier-ignore
  Object.freeze({ value: "power", label: "Power", part: "1N5400–1N5408" }), // prettier-ignore
  Object.freeze({ value: "schottky", label: "Schottky", part: "1N5817–1N5819" }), // prettier-ignore
]);

/** The grade a diode is with none stored. */
export const DEFAULT_DIODE_GRADE = "small-signal";

/** A diode's grade: its params' when it is one, else the default. */
export function diodeGrade(params) {
  return DIODE_GRADES.some((g) => g.value === params?.grade)
    ? params.grade
    : DEFAULT_DIODE_GRADE;
}

/**
 * Spice Lite's Grade field on a plain diode: which kind of diode it is. Shown
 * only while Spice Lite is on (`spiceOnly`) — the digital engine's diode has
 * no curve and no rating — and stored only off the default.
 */
const DIODE_GRADE_FIELD = Object.freeze({
  key: "grade",
  label: "Grade",
  type: "select",
  default: DEFAULT_DIODE_GRADE,
  spiceOnly: true,
  options: Object.freeze(
    DIODE_GRADES.map((g) =>
      Object.freeze({ value: g.value, label: g.label, detail: g.part }),
    ),
  ),
});

/** What both diodes' blurbs say about what a diode IS here. */
const DIODE_NOTE =
  "In this logic sim a diode is ONE-WAY: a HIGH on the anode passes to the " +
  "cathode at the strength it arrived with — from a rail or an output it " +
  "drives, through a resistor it only pulls — and nothing ever passes back. " +
  "A LOW or undriven anode leaves the cathode to whatever else is on it, so " +
  "two diodes into one net with a pull-down resistor make a diode-OR. No " +
  "forward drop is modelled. Like an LED, one wired forward straight across " +
  "two strongly driven nets (rail to rail, an output into ground) burns. " +
  "Under Spice Lite it is a silicon junction: it conducts from about 0.6 V " +
  "(a Schottky from 0.2 V), " +
  "carries what the circuit pushes through it, and burns by its junction's " +
  "temperature.";

function diodeDef({ id, title, blurb, zener }) {
  return {
    id,
    kind: "discrete",
    title,
    blurb,
    group: "Diodes",
    // A DO-35/DO-41 glass or epoxy body, its leads bent to 0.3 in (7.62 mm)
    // — the footprint the KiCad export gives it.
    footprint: Object.freeze({ offsets: Object.freeze([0, 3]) }),
    rotatable: true,
    // THREE holes, not a resistor's four (Jason, 2026-10-14): a DO-35 body is
    // under 4 mm, so its leads bend down to 0.2 in (5.08 mm) — two pitches,
    // a10 → a12. (A DO-41 1N4001's 5.2 mm body is a squeeze there; the part
    // number is not the seat.) The default seat above stays 0.3 in: a saved
    // diode's pin 2 is DERIVED from it, so moving it would rewire every desk.
    minSpan: 2,
    // The data hook every consumer branches on (the drawing, the burn rule,
    // the schematic, the export) — never an id.
    diode: Object.freeze({ zener }),
    countsAsConnection: true,
    properties: zener
      ? [ZENER_VOLTS_FIELD, PART_NUMBER_FIELD]
      : [DIODE_GRADE_FIELD, PART_NUMBER_FIELD],
    pins: [
      { n: 1, name: "A", role: "anode", detail: "anode" },
      {
        n: 2,
        name: "K",
        role: "cathode",
        detail: "cathode — the end the band marks",
      },
    ],
    normalizeParams(raw) {
      // A Zener's voltage (value-fields.js ZENER_VOLTS_FIELD) — optional,
      // and export-only.
      const zenerVolts = zener
        ? storedValue(raw?.zenerVolts, "volt", undefined)
        : undefined;
      // A plain diode's Grade, stored only off the default.
      const grade = zener ? DEFAULT_DIODE_GRADE : diodeGrade(raw);
      return withPartNumber(
        {
          ...(zenerVolts != null ? { zenerVolts } : {}),
          ...(grade !== DEFAULT_DIODE_GRADE ? { grade } : {}),
          ...leadGeometry(raw),
        },
        raw,
      );
    },
    // It joins no net: a diode passes a level one way, which no bridge in
    // the netlist can say.
    internalBridges() {
      return [];
    },
    // …so it says so here instead, as DATA: each pair passes a HIGH from its
    // first pin (the anode) to its second (the cathode) and nothing back
    // (sim/engine.js `buildContext` → `resolveAll`).
    oneWayBridges() {
      return [[1, 2]];
    },
  };
}

/** The bridge rectifier's part number until the user types another: the part
    it was added as (MCC's DB105 — the DB-1 family's 600 V member). */
export const BRIDGE_PART_NUMBER = "DB105";

/**
 * The bridge rectifier: MCC's DB-1 package (DB101–DB107, the same four
 * diodes in each, rated 50–1000 V), a four-lead DIP 0.2 in along a side and
 * 0.3 in across — seated across the channel like the tactile button, with
 * the DIP machinery (footprints.js "DB-4"). Pins in DIP order from the
 * notched corner, as the sheet's top view has them: + · − along one side,
 * ~ · ~ along the other.
 *
 * Four diodes, so it is four one-way pairs: each AC pin passes a HIGH to +,
 * and − passes a HIGH to each AC pin. Under Spice Lite each is a junction of
 * the solve — a rectifier's (spice/diodes.js BRIDGE_SPEC, on a simpler table
 * than the rectifier grade's), not the small signal diode's.
 */
function bridgeRectifierDef() {
  return {
    id: "bridge-rectifier",
    kind: "discrete",
    title: "Bridge rectifier",
    blurb:
      "A 1 A bridge rectifier in a 4-pin DIP (MCC's DB105, 600 V — set " +
      "another of the DB101–DB107 family as its part number). It straddles " +
      "the channel on legs two columns apart: + (pin 1, by the notched " +
      "corner) and − (pin 2) in row e, the two AC pins (~) facing them in " +
      "row f. Four diodes inside: each ~ pin conducts to +, and − conducts " +
      "to each ~ pin, so whichever way the AC swings, + is the top of it and " +
      "− the bottom. In the logic sim each is a one-way diode — a HIGH on " +
      "either ~ pin reaches +, and − is never pulled LOW by it; no forward " +
      "drop. Under Spice Lite each is a rectifier's silicon junction, about " +
      "0.85 V at half an amp, and the output is the AC less two drops. " +
      "Press R with it selected to turn it half a lap.",
    group: "Diodes",
    // The data hook the junction model, the drawing and the export branch
    // on — never the id. Not `diode`: that is the two-lead part's.
    rectifier: true,
    countsAsConnection: true,
    datasheet: "DB105",
    package: "DB-4",
    properties: [PART_NUMBER_FIELD],
    // + is where two diodes' cathodes meet and − where two anodes do, so
    // each takes that role; the AC pins are both, and so plain leads.
    pins: [
      { n: 1, name: "+", role: "cathode", detail: "+ — DC out, by the notch" },
      { n: 2, name: "-", role: "anode", detail: "− — DC return" },
      { n: 3, name: "~", role: "lead", detail: "AC in" },
      { n: 4, name: "~", role: "lead", detail: "AC in" },
    ],
    normalizeParams(raw) {
      return {
        partNumber: partNumberOf(raw) ?? BRIDGE_PART_NUMBER,
        // A chip-style half-lap flip, stored only when set.
        ...(raw?.rot === 180 ? { rot: 180 } : {}),
      };
    },
    // It joins no net: four diodes pass levels one way (below).
    internalBridges() {
      return [];
    },
    // Anode → cathode, as the diode's pair: ~ → + twice, − → ~ twice.
    oneWayBridges() {
      return [
        [3, 1],
        [4, 1],
        [2, 3],
        [2, 4],
      ];
    },
  };
}

// ── Transistors ─────────────────────────────────────────────────────────────

/**
 * The three pins of a TO-92, in the ONE generic order this app draws: the
 * control in the middle, the switched pins either side (E·B·C, S·G·D — the
 * order a 2N2222, 2N3904, 2N3906 or 2N7000 has). It is not any one maker's
 * pinout: a BC547 is C·B·E, and a transistor turns end-for-end with R. The
 * KiCad export numbers its TO-92 pads in this order and says to check them.
 *
 * The control is an `input` (it reads a level and drives nothing); the
 * switched pins are `io`, an analog switch's terminals, which is what they
 * are to the engine.
 */
const BJT_PINS = (npn) => [
  {
    n: 1,
    name: "E",
    role: "io",
    detail: npn ? "emitter — usually to GND" : "emitter — usually to +V",
  },
  {
    n: 2,
    name: "B",
    role: "input",
    detail: npn
      ? "base — HIGH turns it on; floating, it is off"
      : "base — LOW turns it on; floating, it is off",
  },
  { n: 3, name: "C", role: "io", detail: "collector — to the load" },
];
const FET_PINS = (nChannel) => [
  {
    n: 1,
    name: "S",
    role: "io",
    detail: nChannel ? "source — usually to GND" : "source — usually to +V",
  },
  {
    n: 2,
    name: "G",
    role: "input",
    detail: nChannel
      ? "gate — HIGH turns it on; floating, it holds its last state"
      : "gate — LOW turns it on; floating, it holds its last state",
  },
  { n: 3, name: "D", role: "io", detail: "drain — to the load" },
];

/**
 * The packages a transistor can be drawn and exported in, the default first:
 * a MOSFET a TO-220 (the power part an IRLZ44N or an IRF520 is) or a TO-92 (a
 * 2N7000, a BS170); a BJT a TO-92 (a 2N3904) or a TO-220 (a TIP120, a
 * TIP31C). Both stand over three holes in a row — a TO-220's legs are on
 * 0.1 in too. A MOSFET always stores its package; a BJT only a TO-220, so
 * every document from before it had the choice reads as it did.
 */
export const MOSFET_CASES = Object.freeze(["TO-220", "TO-92"]);
export const BJT_CASES = Object.freeze(["TO-92", "TO-220"]);

/** The Package field, its options package NAMES, the same in every
    language; a BJT's shows its default when none is stored. */
const caseField = (cases) =>
  Object.freeze({
    key: "case",
    label: "Package",
    type: "segmented",
    default: cases[0],
    options: Object.freeze(
      cases.map((value) => Object.freeze({ value, label: value })),
    ),
    // A TO-220 cannot stand on BENT legs (free-legs.js): its option is
    // greyed, saying this, while a TO-92's legs are out of a row
    // (desk-controller.js #propertyFieldsFor) — and refused if it is asked
    // for anyway.
    refused:
      "Its legs are bent, and a TO-220's are a rigid row — stand them in " +
      "three holes in a row, in order, first.",
  });

/**
 * Each transistor type's Spice Lite GRADES (features/done/spice-lite-3-plan.md,
 * "Spice-only properties"): which representative part's figures it
 * simulates with (sim/spice/transistors.js holds them). The first is a
 * TO-92's default, Power a TO-220's (Jason, 2026-10-08). The labels are the
 * catalog's English (`properties.option.<value>` translates them); `part`
 * is printed beside each, the same in every language.
 */
export const TRANSISTOR_GRADES = Object.freeze({
  npn: Object.freeze([
    Object.freeze({ value: "small-signal", label: "Small signal", part: "2N3904" }), // prettier-ignore
    Object.freeze({ value: "general", label: "General purpose", part: "2N2222A" }), // prettier-ignore
    Object.freeze({ value: "darlington", label: "Darlington", part: "TIP120" }),
    Object.freeze({ value: "power", label: "Power", part: "TIP31C" }),
  ]),
  pnp: Object.freeze([
    Object.freeze({ value: "small-signal", label: "Small signal", part: "2N3906" }), // prettier-ignore
    Object.freeze({ value: "general", label: "General purpose", part: "2N2907A" }), // prettier-ignore
    Object.freeze({ value: "darlington", label: "Darlington", part: "TIP125" }),
    Object.freeze({ value: "power", label: "Power", part: "TIP32C" }),
  ]),
  nmos: Object.freeze([
    Object.freeze({ value: "logic", label: "Logic level", part: "2N7000" }),
    Object.freeze({ value: "logic-power", label: "Logic-level power", part: "IRLZ44N" }), // prettier-ignore
    Object.freeze({ value: "power", label: "Power", part: "IRF540N" }),
  ]),
  pmos: Object.freeze([
    Object.freeze({ value: "logic", label: "Logic level", part: "BS250" }),
    Object.freeze({ value: "power", label: "Power", part: "IRF9540N" }),
  ]),
});

/** A transistor's grade when none is stored: Power in a TO-220, else its
    type's first. */
export function defaultGrade(type, pkg) {
  const grades = TRANSISTOR_GRADES[type] ?? [];
  if (pkg === "TO-220" && grades.some((g) => g.value === "power")) return "power"; // prettier-ignore
  return grades[0]?.value ?? null;
}

/** The `grade` a CUSTOM grade stores (features/component-value-entry-spec.md
    §5): its figures are in `params.custom`. */
export const CUSTOM_GRADE = "custom";

/**
 * The grade a transistor simulates as under Spice Lite: its stored `grade`
 * when its type has it, else its package's default — and a CUSTOM one's
 * base grade (`custom.from`), for anything that wants a grade and not its
 * figures (transistorCustom is the rest of it).
 * @param {object|null} def
 * @param {object} [params]
 */
export function transistorGrade(def, params) {
  const type = def?.transistor?.type;
  const grades = TRANSISTOR_GRADES[type] ?? [];
  const custom = transistorCustom(def, params);
  if (custom) return custom.from;
  if (grades.some((g) => g.value === params?.grade)) return params.grade;
  return defaultGrade(type, transistorCase(def, params));
}

/**
 * A transistor's CUSTOM figures (`{from, …figures}` — spice/transistor-
 * figures.js), or null when it is one of its grades. The model is built from
 * them (`transistorModelFor`).
 * @param {object|null} def
 * @param {object} [params]
 */
export function transistorCustom(def, params) {
  if (params?.grade !== CUSTOM_GRADE) return null;
  return customFigures(def?.transistor?.type, params.custom);
}

/**
 * A stored custom figure set as the loader keeps it: its base grade one of
 * the type's, and each figure a number within its field's range — else the
 * base grade's own figure, so a damaged set still runs as the part it was
 * started from. Null for no set, or a base the type does not have.
 * @param {string} type
 * @param {unknown} raw
 */
function customFigures(type, raw) {
  const kind = figureKind(type);
  const from = raw?.from;
  if (!kind || !(TRANSISTOR_GRADES[type] ?? []).some((g) => g.value === from)) {
    return null;
  }
  const own = gradeFigures(type, from);
  const out = { from };
  for (const { key, range } of FIGURE_FIELDS[kind]) {
    const v = raw[key];
    out[key] =
      typeof v === "number" && v >= range.min && v <= range.max ? v : own[key];
  }
  return out;
}

/** Each Custom figure's label — the catalog's English, which
    `properties.field.<key>` translates. */
const FIGURE_LABELS = Object.freeze({
  hfe: "Gain hFE",
  vbeOn: "Base turn-on VBE",
  vceo: "Breakdown VCEO",
  icMax: "Max current IC",
  vth: "Threshold VGS(th)",
  rdsOn: "On-resistance RDS(on)",
  vdsMax: "Breakdown V(BR)DSS",
  idMax: "Max current ID",
});

/**
 * The Grade field (spiceOnly): a PRESET list — its type's grades, each with
 * its representative part beside it, its default the package's — ending in
 * Custom…, which opens the grade's figures to edit
 * (components/preset-field.js, part-properties-dialog.js's `"preset"` type;
 * features/component-value-entry-spec.md §5). Its commits are patches of
 * `grade` and `custom` together.
 */
const gradeField = (type, cases) =>
  Object.freeze({
    key: "grade",
    label: "Grade",
    type: "preset",
    spiceOnly: true,
    default: (values) => defaultGrade(type, caseAmong(cases, values)),
    options: Object.freeze(
      TRANSISTOR_GRADES[type].map((g) =>
        Object.freeze({ value: g.value, label: g.label, detail: g.part }),
      ),
    ),
    customValue: CUSTOM_GRADE,
    customLabel: "Custom…",
    figures: Object.freeze(
      FIGURE_FIELDS[figureKind(type)].map((f) =>
        Object.freeze({ ...f, label: FIGURE_LABELS[f.key] }),
      ),
    ),
    /** The preset the card shows — or Custom…, for figures of their own. */
    selected(values) {
      if (
        values?.grade === CUSTOM_GRADE &&
        customFigures(type, values.custom)
      ) {
        return CUSTOM_GRADE;
      }
      return TRANSISTOR_GRADES[type].some((g) => g.value === values?.grade)
        ? values.grade
        : defaultGrade(type, caseAmong(cases, values));
    },
    /** The figures the Custom fields show: the custom set's, or the shown
        grade's own. */
    figuresOf(values) {
      const custom =
        values?.grade === CUSTOM_GRADE ? customFigures(type, values.custom) : null; // prettier-ignore
      if (custom) return custom;
      const from = this.selected(values);
      return { from, ...gradeFigures(type, from) };
    },
    /** What picking an entry sets: a grade (its figures gone), or Custom…
        started from the grade shown just before it. */
    pick(values, value) {
      if (value !== CUSTOM_GRADE) return { grade: value, custom: null };
      return { grade: CUSTOM_GRADE, custom: this.figuresOf(values) };
    },
    /** What a typed figure sets: the whole custom set, that one changed. */
    setFigure(values, key, number) {
      return {
        grade: CUSTOM_GRADE,
        custom: { ...this.figuresOf(values), [key]: number },
      };
    },
  });

/**
 * The package a transistor is in: its params' choice among its def's
 * `transistor.cases`, else the first of them. The one place it is read, so
 * the drawing, the box, the BOM and the export can never disagree.
 * @param {object|null} def
 * @param {{case?: unknown}|null} [params]
 * @returns {string|null}
 */
export function transistorCase(def, params) {
  return caseAmong(def?.transistor?.cases ?? [], params);
}

/**
 * A bent TO-92's `legs` — pins 2 and 3 as `{dx, dy}` pitch offsets from pin
 * 1's hole, each through the one lead coercion (lead-offset.js) — or null
 * when there are none, or they are junk, or two legs share a point.
 */
export function transistorLegs(raw) {
  if (!Array.isArray(raw) || raw.length !== 2) return null;
  const legs = raw.map(normalizeLeadOffset);
  if (legs.some((leg) => !leg)) return null;
  if (legs[0].dx === legs[1].dx && legs[0].dy === legs[1].dy) return null;
  return Object.freeze(legs);
}

/** `params.case` when it is one of `cases`, else the first of them. */
const caseAmong = (cases, params) =>
  cases.includes(params?.case) ? params.case : (cases[0] ?? null);

/** What every transistor's blurb says about how it is placed. */
const placementNote = (cases) =>
  (cases.length > 1
    ? `A ${cases[0]} (or a ${cases.slice(1).join(", ")} — pick its Package ` +
      "in Properties) standing over three holes in a row"
    : `A ${cases[0]} standing over three holes in a row`) +
  "; its pin letters are printed on it, and R with it selected turns it " +
  "end-for-end. A TO-92's legs bend: Option/Alt-drag one to any hole. " +
  "Real pinouts differ by part number, so check yours. In the " +
  "digital sim no gain, threshold, saturation or on-resistance is " +
  "modelled — put a resistor in an LED's leg as you would on a bench. Set " +
  "an optional part number in Properties.";

/** The four transistors are one part to the user, its Type swapped in place
    (value-fields.js `partTypeField`) — four kinds, so a list, not a track. */
const TRANSISTORS = Object.freeze(["npn", "pnp", "nmos", "pmos"]);
const TRANSISTOR_TYPE_FIELD = partTypeField(
  [
    { value: "npn", label: "NPN" },
    { value: "pnp", label: "PNP" },
    { value: "nmos", label: "N-channel MOSFET" },
    { value: "pmos", label: "P-channel MOSFET" },
  ],
  "select",
);

/** What a transistor is under Spice Lite, where it is no switch. */
const BJT_SPICE_NOTE =
  "Under Spice Lite it is a transistor of the Grade its Properties pick " +
  "(small signal by default, power in a TO-220; a Darlington too): its base " +
  "conducts from about 0.6 V (1.2 V a Darlington), its collector carries " +
  "its gain times the base current as far as the circuit allows, and it " +
  "saturates as its datasheet's part does.";
const MOSFET_SPICE_NOTE =
  "Under Spice Lite its channel opens past its Grade's gate threshold " +
  "(logic level by default, power in a TO-220) along its datasheet's " +
  "transfer curve, and its gate keeps the voltage it was last driven to.";

/**
 * One transistor def. `type` names it (the data hook the drawing, the
 * schematic and the export read); `onLevel` is what its control must read to
 * turn it on; `holds` marks a MOSFET, whose gate keeps its last state.
 */
function transistorDef({ id, title, blurb, type, onLevel, holds }) {
  const pins = holds ? FET_PINS(onLevel === H) : BJT_PINS(onLevel === H);
  const cases = holds ? MOSFET_CASES : BJT_CASES;
  return {
    id,
    kind: "discrete",
    title,
    blurb: `${blurb} ${holds ? MOSFET_SPICE_NOTE : BJT_SPICE_NOTE} ${placementNote(cases)}`, // prettier-ignore
    group: "Transistors",
    footprint: Object.freeze({ offsets: Object.freeze([0, 1, 2]) }),
    // Turns end-for-end in place (params.rot 180), as the resistor array
    // does: the same three holes, the pin order reversed.
    reversible: true,
    // A TO-92's three legs are wire: each bends to a hole of its own, one at
    // a time (an Option/Alt drag, as a resistor's leg — Jason, 2026-10-14).
    // A TO-220's are a rigid row, so it never answers. Bent, the part stores
    // `params.legs` — where pins 2 and 3 went, as pitch offsets from pin 1's
    // hole (model/free-legs.js); without it, it is the straight row above.
    freeLegs: (params) => caseAmong(cases, params) === "TO-92",
    // `cases` are the packages it can be drawn and exported in
    // (transistorCase).
    transistor: Object.freeze({ type, holds, cases }),
    // A base or gate nothing drives is UNDEFINED to the switch, never the
    // HIGH a TTL input would read (catalog/families.js `floatsUnknown`).
    floating: "unknown",
    countsAsConnection: true,
    swapsWith: TRANSISTORS,
    // The part number is a combo of this type's common parts
    // (value-fields.js `transistorPartField`) — still only a label.
    properties: [
      TRANSISTOR_TYPE_FIELD,
      caseField(cases),
      gradeField(type, cases),
      transistorPartField(type),
    ],
    pins,
    // Swapped in from another type (DeskDoc.setComponentRef): a part number
    // that type's list holds names a part this one is not, so it goes; one
    // on no list is the user's, and stays.
    adoptParams(params) {
      // A custom grade's figures are a BJT's or a MOSFET's: across that
      // line they mean nothing, and the part takes its own default.
      let out = params;
      const from = params?.custom?.from;
      if (
        params?.grade === CUSTOM_GRADE &&
        !TRANSISTOR_GRADES[type].some((g) => g.value === from)
      ) {
        // prettier-ignore
        const { grade: _g, custom: _c, ...rest } = out;
        out = rest;
      }
      const owner = transistorTypeOf(out?.partNumber);
      if (!owner || owner === type) return out;
      const { partNumber: _dropped, ...rest } = out;
      return rest;
    },
    normalizeParams(raw) {
      // A MOSFET's package is always stored (a document has always held
      // one); a BJT's only when it is not the TO-92 every BJT once was. The
      // Spice Lite grade whenever one was picked — even the package's own
      // default, or switching the package away and back silently swapped a
      // grade the user chose for the new package's; with none stored, the
      // grade follows the package.
      // Bent legs (`legs`) are a TO-92's alone — a TO-220 cannot stand on
      // them — so a part that has them IS a TO-92, whatever else it says,
      // and the end-for-end turn is in the legs themselves.
      const legs = transistorLegs(raw?.legs);
      const pkg = legs ? "TO-92" : caseAmong(cases, raw);
      // A CUSTOM grade keeps its whole figure set — unless the set is its
      // base grade's own, when it IS that grade, and is stored as one (a
      // card reopened never shows a preset over figures that are not it,
      // nor Custom… over figures that are).
      const custom = raw?.grade === CUSTOM_GRADE ? customFigures(type, raw.custom) : null; // prettier-ignore
      const own = custom && isGradeFigures(type, custom.from, custom);
      const grade = custom
        ? own ? custom.from : null // prettier-ignore
        : TRANSISTOR_GRADES[type].some((g) => g.value === raw?.grade) ? raw.grade : null; // prettier-ignore
      return withPartNumber(
        {
          ...(raw?.rot === 180 && !legs ? { rot: 180 } : {}),
          ...(holds || pkg !== cases[0] ? { case: pkg } : {}),
          ...(legs ? { legs } : {}),
          ...(custom && !own ? { grade: CUSTOM_GRADE, custom } : {}),
          ...(grade ? { grade } : {}),
        },
        raw,
      );
    },
    // No bridge: whether it conducts is a question about LEVELS, which only
    // the engine can answer — the channel below.
    internalBridges() {
      return [];
    },
    logic: transistorSwitch({ control: 2, a: 1, b: 3, onLevel, holds }),
  };
}

export const DISCRETE_DEFS = Object.freeze(
  [
    {
      id: "cap-ceramic",
      kind: "discrete",
      title: "Capacitor (ceramic)",
      blurb:
        "Ceramic disc capacitor — non-polarised, either way round. Set its " +
        "Capacitance in Properties (100p, 10n, 4.7µ, 4u7…), and an optional " +
        "part number. " +
        CAPACITOR_NOTE +
        " Press R while placing to stand it up and pick two free ends.",
      group: "Capacitors",
      // A disc's leads at 2.5 mm (0.1 in) — adjacent holes, as the
      // electrolytic's are.
      footprint: Object.freeze({ offsets: Object.freeze([0, 1]) }),
      rotatable: true,
      minSpan: 1,
      // The data hook every consumer branches on (the trace, the drop note,
      // the BOM, the export) — never an id.
      capacitor: Object.freeze({ polarized: false }),
      countsAsConnection: true,
      swapsWith: CAPACITORS,
      properties: [
        CAPACITOR_TYPE_FIELD,
        capacitanceField(VALUE_RANGES.ceramic, CERAMIC_VALUES),
        PART_NUMBER_FIELD,
      ],
      pins: [
        { n: 1, name: "1", role: "lead" },
        { n: 2, name: "2", role: "lead" },
      ],
      normalizeParams: (raw) => capacitorParams(raw, 100e-9),
      // A non-connect, always: no bridge, hard or weak.
      internalBridges() {
        return [];
      },
    },
    {
      id: "cap-electrolytic",
      kind: "discrete",
      title: "Capacitor (electrolytic)",
      blurb:
        "Aluminium electrolytic capacitor — POLARISED: pin 1 is +, pin 2 is " +
        "−, which the stripe down its side marks. Set its Capacitance in " +
        "Properties (1µ, 10µ, 4u7, 470µ…), and an optional part number. " +
        CAPACITOR_NOTE +
        " Its polarity is drawn and exported; wiring it backwards changes " +
        "nothing in the sim. Press R while placing to stand it up and pick " +
        "two free ends.",
      group: "Capacitors",
      // A small radial can's leads at 2.5 mm (0.1 in).
      footprint: Object.freeze({ offsets: Object.freeze([0, 1]) }),
      rotatable: true,
      minSpan: 1,
      capacitor: Object.freeze({ polarized: true }),
      countsAsConnection: true,
      swapsWith: CAPACITORS,
      properties: [
        CAPACITOR_TYPE_FIELD,
        capacitanceField(VALUE_RANGES.electrolytic, ELECTROLYTIC_VALUES),
        PART_NUMBER_FIELD,
      ],
      pins: [
        { n: 1, name: "+", role: "lead" },
        { n: 2, name: "-", role: "lead" },
      ],
      normalizeParams: (raw) => capacitorParams(raw, 10e-6),
      internalBridges() {
        return [];
      },
    },
    diodeDef({
      id: "diode",
      title: "Diode",
      blurb:
        "Diode — anode at pin 1, cathode at pin 2, the end the band marks. " +
        "Under Spice Lite its Grade (Properties) picks which kind: small " +
        "signal (a 1N4148, the default), a 1 A rectifier (1N4001–1N4007), " +
        "a 3 A power rectifier (1N5400–1N5408) or a Schottky " +
        "(1N5817–1N5819) — its forward drop, and the current it burns at. " +
        DIODE_NOTE +
        " Press R while placing to stand it up and pick two free ends.",
      zener: false,
    }),
    diodeDef({
      id: "zener",
      title: "Zener diode",
      blurb:
        "Zener diode — anode at pin 1, cathode at pin 2, the end the band " +
        "marks. Set its Zener voltage (5.1V, 5V1, 3.3…) in Properties; it is " +
        "printed and exported. In the digital sim it is exactly a diode: " +
        "reverse breakdown — what a Zener is for — is analog, so it regulates " +
        "nothing there; under Spice Lite it conducts backwards at its Zener " +
        "voltage and clamps. " +
        DIODE_NOTE +
        " Press R while placing to stand it up and pick two free ends.",
      zener: true,
    }),
    bridgeRectifierDef(),
    {
      id: "inductor",
      kind: "discrete",
      title: "Inductor",
      blurb:
        "Axial inductor (a choke). Set an optional Inductance (100n, 10µ, " +
        "4u7, 100m, 1H…) and part number in Properties; both are printed and " +
        "exported. Properties also picks which it is — a Coil (copper wound " +
        "round a ferrite ring) or a Can (a drum in a black sleeve, its value " +
        "printed on top) — and how many holes its body covers between its " +
        "leads (1, 2 or 3, the smallest part to the biggest). " +
        "In the digital sim it conducts exactly like a WIRE — its two leads " +
        "are one net — because at DC that is what a coil is, so one wired " +
        "across the rails is a short. Under Spice Lite one with an " +
        "Inductance is a real inductor: its current cannot change in an " +
        "instant, it rises and dies away through its winding's resistance " +
        "(Properties ▸ Winding, shown under Spice Lite), and switched off " +
        "with no diode across it, it kicks the transistor that switched it " +
        "into breakdown. With no Inductance it is a wire under both. Press R " +
        "while placing to stand it up and pick two free ends.",
      group: "Inductors",
      // Leads 0.3 in (7.62 mm) apart — or 0.2 / 0.4 in, set to one or three
      // holes between them (`offsetsFor`, read through catalog/index.js
      // `footprintOffsets`).
      footprint: Object.freeze({ offsets: INDUCTOR_OFFSETS[INDUCTOR_DEFAULT_HOLES] }), // prettier-ignore
      offsetsFor: (params) => INDUCTOR_OFFSETS[inductorHoles(params)],
      rotatable: true,
      // Two pitches: the 1-hole part's own leads, lying along a row.
      minSpan: 2,
      inductor: true,
      countsAsConnection: true,
      properties: [
        INDUCTANCE_FIELD,
        INDUCTOR_STYLE_FIELD,
        BODY_HOLES_FIELD,
        WINDING_FIELD,
        PART_NUMBER_FIELD,
      ],
      pins: [
        { n: 1, name: "1", role: "lead" },
        {
          n: 2,
          name: "2",
          role: "lead",
          detail:
            "2, 3 or 4 holes along from pin 1 (1, 2 or 3 between the leads)",
        },
      ],
      normalizeParams(raw) {
        const henries = storedValue(raw?.henries, "henry", undefined);
        return withPartNumber(
          {
            ...(henries != null ? { henries } : {}),
            style: INDUCTOR_STYLES.includes(raw?.style)
              ? raw.style
              : INDUCTOR_STYLES[0],
            bodyHoles: inductorHoles(raw),
            // Spice Lite's Winding, stored only when it is not Typical.
            ...(inductorWinding(raw) !== DEFAULT_WINDING ? { winding: inductorWinding(raw) } : {}), // prettier-ignore
            ...leadGeometry(raw),
          },
          raw,
        );
      },
      // A WIRE: the two leads are one net, in the netlist as on the bench.
      internalBridges() {
        return [[1, 2]];
      },
    },
    transistorDef({
      id: "npn",
      title: "NPN transistor (BJT)",
      blurb:
        "NPN bipolar transistor, pins E · B · C. In this logic sim it is a " +
        "SWITCH: while the base reads HIGH, collector and emitter are joined; " +
        "otherwise they are apart. A base that is floating or undefined " +
        "passes no current, so the transistor is off — a BJT remembers " +
        "nothing.",
      type: "npn",
      onLevel: H,
      holds: false,
    }),
    transistorDef({
      id: "pnp",
      title: "PNP transistor (BJT)",
      blurb:
        "PNP bipolar transistor, pins E · B · C. In this logic sim it is a " +
        "SWITCH: while the base reads LOW, emitter and collector are joined; " +
        "otherwise they are apart. A base that is floating or undefined " +
        "passes no current, so the transistor is off — a BJT remembers " +
        "nothing.",
      type: "pnp",
      onLevel: L,
      holds: false,
    }),
    transistorDef({
      id: "nmos",
      title: "N-channel MOSFET",
      blurb:
        "N-channel enhancement MOSFET, pins S · G · D. In this logic sim it " +
        "is a SWITCH: while the gate reads HIGH, drain and source are joined; " +
        "while it reads LOW they are apart. The gate stores charge, so a gate " +
        "left floating or undefined HOLDS the last state it was driven to — " +
        "off until it has been driven at all — and the part shows that it is " +
        "holding. On a real board that charge leaks away: tie the gate down " +
        "with a resistor.",
      type: "nmos",
      onLevel: H,
      holds: true,
    }),
    transistorDef({
      id: "pmos",
      title: "P-channel MOSFET",
      blurb:
        "P-channel enhancement MOSFET, pins S · G · D. In this logic sim it " +
        "is a SWITCH: while the gate reads LOW, source and drain are joined; " +
        "while it reads HIGH they are apart. The gate stores charge, so a " +
        "gate left floating or undefined HOLDS the last state it was driven " +
        "to — off until it has been driven at all — and the part shows that " +
        "it is holding. On a real board that charge leaks away: tie the gate " +
        "up with a resistor.",
      type: "pmos",
      onLevel: L,
      holds: true,
    }),
  ].map(Object.freeze),
);
